import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'

/**
 * MentorHome — editorial overview: professional identity, assigned projects,
 * recent participant activity. Scoped entirely by /project-lab/mentor/* APIs.
 */
export default function MentorHome({ mentor }) {
  const [projects, setProjects] = useState(null)
  const [participants, setParticipants] = useState(null)

  useEffect(() => {
    api.get('/project-lab/mentor/projects')
      .then((r) => setProjects(r.data.projects))
      .catch(() => setProjects([]))
    api.get('/project-lab/mentor/participants')
      .then((r) => setParticipants(r.data.participants))
      .catch(() => setParticipants([]))
  }, [])

  if (projects === null || participants === null) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>
  }

  return (
    <div className="max-w-4xl space-y-10">
      {/* Identity */}
      <header className="space-y-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-cinema-gold font-medium">
          Project Lab Mentor
        </p>
        <h1 className="font-display text-2xl sm:text-3xl text-white">{mentor.professional_name}</h1>
        {mentor.professional_title && (
          <p className="text-sm text-cinema-soft">{mentor.professional_title}</p>
        )}
      </header>

      {/* Assigned projects */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
          Assigned Projects
        </h2>
        {projects.length === 0 ? (
          <p className="text-sm text-cinema-muted py-6">
            No projects assigned yet. SMA administration will assign you to a Project Lab production.
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/mentor/projects/${p.id}`}
                className="flex items-center justify-between gap-4 py-4 group"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white group-hover:text-cinema-gold transition-colors truncate">
                    {p.title}
                  </p>
                  <p className="text-xs text-cinema-muted mt-0.5">
                    {p.discipline || 'Project Lab'} · {p.status}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm text-white font-mono">{p.active_participants}</p>
                  <p className="text-[10px] uppercase tracking-wider text-cinema-muted">participants</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Recent participant activity */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
          Participants
        </h2>
        {participants.length === 0 ? (
          <p className="text-sm text-cinema-muted py-6">No participants assigned yet.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {participants.slice(0, 10).map((pt) => (
              <div key={pt.enrollment_id} className="flex items-center justify-between gap-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">
                    {pt.student_first_name} {pt.student_last_name}
                  </p>
                  <p className="text-xs text-cinema-muted truncate">{pt.project_title}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-cinema-soft">
                    {pt.current_stage_title || `Stage ${pt.current_stage_index}`}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-cinema-muted">
                    {pt.current_stage_status || pt.enrollment_status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {participants.length > 10 && (
          <Link to="/mentor/participants" className="inline-block mt-4 text-xs text-cinema-gold hover:underline">
            View all {participants.length} participants →
          </Link>
        )}
      </section>
    </div>
  )
}
